import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClipLoader from "react-spinners/ClipLoader";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CsvToHtmlTable } from "../../../../react-csv-to-table";

/** Trigger a client-side file download of the given text content. */
const downloadTextFile = (content: string, filename: string, mime: string) => {
  const blob = new Blob([content], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Result shape returned by the parent's per-field fetch function. Mirrors the
 * data produced by the existing single-field preview logic (getFragment /
 * getTable) but returned as a value so we can collect results for every field
 * at once instead of writing to shared component state.
 */
export type PreviewFieldResult =
  | { status: "ok"; isTable: false; text: string }
  | { status: "ok"; isTable: true; rowCount: number; rows: string[][] }
  | { status: "empty" }
  | { status: "error"; error: string };

export type PreviewField = {
  id: string;
  name: string;
  fieldType?: string;
};

type RowState =
  | { status: "idle" }
  | { status: "loading" }
  | ({ status: "done" } & PreviewFieldResult);

type Props = {
  fields: PreviewField[];
  fetchFieldValue: (id: string) => Promise<PreviewFieldResult>;
  currentPage: number;
  /** The field currently selected on the canvas — the one Preview runs on. */
  selectedFieldId?: string | null;
  /** Page/viewport is rendered and ready to extract from. */
  ready?: boolean;
};

/**
 * "Does my template work?" panel. Previews the CURRENTLY SELECTED field on the
 * example page and shows field name -> extracted value in a compact table. Each
 * preview runs one server-side extraction task, so we deliberately run only the
 * selected field (not all of them at once) to keep it fast and cheap. The table
 * accumulates results as you select and preview each field in turn. Table fields
 * collapse to a row count that expands to show the rows.
 */
export const PreviewAllFields: React.FC<Props> = ({
  fields,
  fetchFieldValue,
  currentPage,
  selectedFieldId,
  ready,
}) => {
  const [results, setResults] = useState<Record<string, RowState>>({});
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [hasRun, setHasRun] = useState(false);

  const selectedField = selectedFieldId
    ? fields.find((f) => f.id === selectedFieldId) || null
    : null;

  const runFor = React.useCallback(
    async (field: PreviewField) => {
      setRunning(true);
      setHasRun(true);
      setResults((prev) => ({ ...prev, [field.id]: { status: "loading" } }));
      try {
        const res = await fetchFieldValue(field.id);
        setResults((prev) => ({
          ...prev,
          [field.id]: { status: "done", ...res },
        }));
      } finally {
        setRunning(false);
      }
    },
    [fetchFieldValue],
  );

  const runSelected = () => {
    if (running || !selectedField) return;
    runFor(selectedField);
  };

  // Auto-preview the field the moment the USER selects or creates it — one
  // field at a time. Deliberately does NOTHING on load: the field auto-selected
  // while the template loads becomes the baseline (recorded, not previewed).
  // Also waits until the page/viewport is ready, otherwise the extraction just
  // returns "Open the page first".
  //
  // The tracked key includes the field's TYPE, not just its id. A freshly DRAWN
  // field is selected on the canvas BEFORE the user picks its type, and it only
  // appears in `fields` once finalized (still-configuring fields are filtered
  // out upstream). So its id is already the selection when it becomes
  // previewable — keying on id alone would never re-fire and the new field's
  // value would stay blank until a manual "Preview field" click. Keying on
  // `${id}:${fieldType}` makes finalization (typeless -> typed) a change we
  // detect, so a newly drawn field auto-previews exactly once when finalized.
  const autoPreviewInitRef = useRef(false);
  const lastAutoPreviewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready) return;
    const current = selectedFieldId ?? null;
    // Previewable only once the selected field is present in `fields` with a
    // resolved type. A still-configuring (typeless) field yields a null key and
    // is skipped, so we never extract an unconfigured region.
    const field = current ? fields.find((f) => f.id === current) : undefined;
    const key = field && field.fieldType ? `${current}:${field.fieldType}` : null;

    if (!autoPreviewInitRef.current) {
      autoPreviewInitRef.current = true;
      lastAutoPreviewedRef.current = key;
      return;
    }
    if (!key || key === lastAutoPreviewedRef.current) return;
    lastAutoPreviewedRef.current = key;
    if (field) runFor(field);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldId, ready, fields]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderValue = (field: PreviewField) => {
    const state = results[field.id];
    if (!state || state.status === "idle") {
      return <span className="text-muted">—</span>;
    }
    if (state.status === "loading") {
      return <ClipLoader size={12} color="#0d6efd" />;
    }
    // status === "done"
    if (state.status === "error") {
      return (
        <span className="text-danger" title={state.error}>
          {state.error}
        </span>
      );
    }
    if (state.status === "empty") {
      return <span className="text-muted">(empty)</span>;
    }
    if (state.isTable) {
      const isOpen = !!expanded[field.id];
      const csv = state.rows.map((row) => row.join(",")).join("\n");
      return (
        <div>
          <button
            type="button"
            onClick={() => toggleExpand(field.id)}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              color: "#0d6efd",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            <FontAwesomeIcon
              icon={isOpen ? "chevron-down" : "chevron-right"}
              className="mr-1"
            />
            {state.rowCount} row{state.rowCount === 1 ? "" : "s"}
          </button>
          {isOpen && state.rows.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <CopyToClipboard text={csv}>
                  <Button variant="outline-primary" size="sm">
                    <FontAwesomeIcon icon="copy" className="mr-1" />
                    Copy
                  </Button>
                </CopyToClipboard>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() =>
                    downloadTextFile(
                      csv,
                      `${field.name || "extracted_data"}.csv`,
                      "text/csv",
                    )
                  }
                >
                  <FontAwesomeIcon icon="download" className="mr-1" />
                  Download
                </Button>
              </div>
              <div
                style={{
                  border: "1px solid #e1e5eb",
                  borderRadius: 4,
                  maxHeight: 220,
                  overflow: "auto",
                }}
              >
                <CsvToHtmlTable
                  rows={state.rows}
                  csvDelimiter=","
                  hasHeader={true}
                  tableClassName="table table-sm table-striped table-hover mb-0"
                  tableRowClassName=""
                  tableColumnClassName=""
                />
              </div>
            </div>
          )}
        </div>
      );
    }
    // plain text
    return (
      <div>
        <span
          title={state.text}
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {state.text.length > 140
            ? `${state.text.slice(0, 140)}…`
            : state.text}
        </span>
        {state.text && (
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <CopyToClipboard text={state.text}>
              <Button variant="outline-primary" size="sm">
                <FontAwesomeIcon icon="copy" className="mr-1" />
                Copy
              </Button>
            </CopyToClipboard>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() =>
                downloadTextFile(
                  state.text,
                  `${field.name || "extracted_data"}.txt`,
                  "text/plain",
                )
              }
            >
              <FontAwesomeIcon icon="download" className="mr-1" />
              Download
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "14px 16px",
        background: "#fff",
        border: "1px solid #b6d4fe",
        borderRadius: 10,
        boxShadow: "0 2px 8px rgba(13, 110, 253, 0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <h4
          style={{
            fontSize: 15,
            fontWeight: 700,
            margin: 0,
            color: "#0a58ca",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FontAwesomeIcon icon="eye" style={{ opacity: 0.9 }} />
          Preview
        </h4>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={runSelected}
          disabled={running || !selectedField}
          title={
            selectedField
              ? `Extract "${selectedField.name}" on this page and check the value`
              : "Select a field to preview it"
          }
          className="d-flex align-items-center"
        >
          {running ? (
            <ClipLoader size={13} />
          ) : (
            <FontAwesomeIcon icon="sync" className="mr-1" />
          )}
          <span className="ml-1">{running ? "Previewing…" : "Preview field"}</span>
        </Button>
      </div>

      <p className="text-muted small mb-2">
        {selectedField ? (
          <>
            Previews <strong>{selectedField.name}</strong> on page {currentPage}.
            Select a different field to preview its value.
          </>
        ) : (
          <>Select a field to preview its extracted value on page {currentPage}.</>
        )}
      </p>

      {fields.length === 0 ? (
        <p className="text-muted small mb-0">
          Add a field to preview extracted values here.
        </p>
      ) : (
        // Cap the height so a template with many fields scrolls inside the
        // panel instead of pushing the field list far down the drawer.
        <div style={{ maxHeight: 260, overflowY: "auto" }}>
        <table
          className="table table-sm mb-0"
          style={{ fontSize: 12, tableLayout: "fixed", width: "100%" }}
        >
          <tbody>
            {fields.map((f) => (
              <tr
                key={f.id}
                style={
                  f.id === selectedFieldId
                    ? { background: "#eef4ff" }
                    : undefined
                }
              >
                <td
                  className="px-1 py-1 font-weight-bold align-top"
                  style={{
                    width: "40%",
                    wordBreak: "break-word",
                    verticalAlign: "top",
                  }}
                >
                  {f.name}
                </td>
                <td
                  className="px-1 py-1 align-top"
                  style={{ verticalAlign: "top" }}
                >
                  {renderValue(f)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default PreviewAllFields;
